import { VIDEO_ENGINE_TIERS, videoEngineLabel } from "@/shared/brand/engineTiers";

/**
 * 视频模块「配音 / 字幕 / 转场」枚举 → 中文标签 的**单一来源**。
 * 之前 VideoCreatePage / VideoTaskDetailPage / VoiceSubtitlePanel 各写一份(且已轻微漂移),
 * 统一到此处。组件按需 import,不再本地复制。
 */

/** CosyVoice v3 音色 id → 展示标签 + 分组(情感可控 / 固定)。 */
export interface VoiceOption {
  value: string;
  label: string;
  /** 情感可控音色(可配合 voiceEmotion);其余为固定音色,emotion 忽略。 */
  emotionControllable: boolean;
}

export const VOICE_OPTIONS: readonly VoiceOption[] = [
  { value: "longanhuan", label: "女声·欢脱元气", emotionControllable: true },
  { value: "longanyang", label: "男声·阳光活力", emotionControllable: true },
  { value: "longhuhu_v3", label: "女童·天真烂漫", emotionControllable: true },
  { value: "longyingxiao_v3", label: "女声·清甜推销", emotionControllable: false },
  { value: "longanran_v3", label: "女声·活泼质感", emotionControllable: false },
  { value: "longanxuan_v3", label: "女声·经典直播", emotionControllable: false },
  { value: "longxiaoxia_v3", label: "女声·沉稳权威", emotionControllable: false },
  { value: "longshuo_v3", label: "男声·博才干练", emotionControllable: false },
  { value: "longshu_v3", label: "男声·沉稳青年", emotionControllable: false },
  { value: "longxiaochun_v3", label: "女声·知性积极", emotionControllable: false },
  { value: "longyingjing_v3", label: "女声·低调冷静", emotionControllable: false },
  { value: "longyue_v3", label: "女声·温暖磁性", emotionControllable: false },
];

export const EMOTION_VOICES = VOICE_OPTIONS.filter((v) => v.emotionControllable).map(
  (v) => v.value
);

export const VOICE_TONE_LABEL: Record<string, string> = Object.fromEntries(
  VOICE_OPTIONS.map((v) => [v.value, v.label])
);

export const EMOTIONS = [
  { value: "happy", label: "愉快" },
  { value: "neutral", label: "中性" },
  { value: "surprised", label: "惊喜" },
  { value: "sad", label: "悲伤" },
  { value: "angry", label: "激昂" },
] as const;
export const EMOTION_LABEL: Record<string, string> = Object.fromEntries(
  EMOTIONS.map((e) => [e.value, e.label])
);

export const FONTS = [
  { value: "heiti", label: "黑体" },
  { value: "songti", label: "宋体" },
  { value: "kaiti", label: "楷体" },
  { value: "yuanti", label: "圆体" },
] as const;
export const FONT_LABEL: Record<string, string> = Object.fromEntries(
  FONTS.map((f) => [f.value, f.label])
);

export const SUBTITLE_SIZES = [
  { value: "small", label: "小" },
  { value: "medium", label: "中" },
  { value: "large", label: "大" },
] as const;
export const SUBTITLE_SIZE_LABEL: Record<string, string> = Object.fromEntries(
  SUBTITLE_SIZES.map((s) => [s.value, s.label])
);

export const SUBTITLE_POSITIONS = [
  { value: "bottom", label: "底部" },
  { value: "center", label: "居中" },
  { value: "top", label: "顶部" },
] as const;
export const SUBTITLE_POSITION_LABEL: Record<string, string> = Object.fromEntries(
  SUBTITLE_POSITIONS.map((p) => [p.value, p.label])
);

/**
 * 视频 provider key → 对客系列名(只在拿不到 model 时兜底)。
 * 单一来源在 `@/shared/brand/engineTiers`;本表是它的静态别名。
 */
export const VIDEO_PROVIDER_LABEL: Readonly<Record<string, string>> = VIDEO_ENGINE_TIERS;

/**
 * provider(+ model,+ 后端直出档位名)→ 对客档位名(「专业·综合」);超管非代客态附真名。
 * ★OEM H2:有后端 label(能力表 `label` / 任务 `modelLabel`)就用它 —— 档位名单一真源在 api;
 *   没有才按 model 查镜像表。不再把 model id 原样拼给用户看。
 */
export function videoProviderLabel(
  provider: string | null | undefined,
  model?: string | null,
  serverLabel?: string | null
): string {
  if (!provider && !model && !serverLabel) return "自动";
  return videoEngineLabel(provider, model, serverLabel);
}

export const TRANSITIONS = [
  { value: "hard", label: "硬切" },
  { value: "fade", label: "淡入淡出" },
] as const;
export const TRANSITION_LABEL: Record<string, string> = Object.fromEntries(
  TRANSITIONS.map((t) => [t.value, t.label])
);

/**
 * 视频生成模式码 → 中文(1004 F23 #7:自由创作页「当前自动选用 … · 模式 T2V」露码)。
 * 与「高级」里生成模式下拉同一张表(此前那里内联三元,这里收成单源)。
 */
export const GENERATION_MODE_LABEL: Record<string, string> = {
  T2V: "文生视频",
  I2V: "图生(首帧)",
  I2V_FIRST_LAST: "图生(首尾帧)",
  R2V_SWAP: "换我出镜",
};

/** 认不出的码不原样给用户看,回落「自动」。 */
export function generationModeLabel(mode: string | null | undefined): string {
  return GENERATION_MODE_LABEL[(mode ?? "").trim().toUpperCase()] ?? "自动";
}

/**
 * 画幅比例的人话(1005 生产实测:图生视频详情页直出英文「adaptive」)。
 * 带首帧/图生动起来时 ratio 发 `adaptive`(画幅跟图走),数字比例原样显示。
 * 空值 → "—"(不印 undefined)。
 */
const RATIO_WORD_LABEL: Readonly<Record<string, string>> = {
  adaptive: "自适应(跟随首帧图)",
  auto: "智能",
  smart: "智能",
};

export function ratioLabel(ratio: string | null | undefined): string {
  const r = (ratio ?? "").trim();
  if (!r) return "—";
  return RATIO_WORD_LABEL[r.toLowerCase()] ?? r;
}
